import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useParams, useNavigate } from "react-router-dom"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../../firebase"
import "./singlecourse.css"
import { ChevronLeft, Star, Clock, Users, GraduationCap, CheckCircle, PlayCircle, BookOpen } from "lucide-react"

const Course = () => {
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const params = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true)
        const courseDoc = await getDoc(doc(db, "courses", params.id))
        if (courseDoc.exists()) {
          setCourse({ id: courseDoc.id, ...courseDoc.data() })
        } else {
          setError("Course not found")
        }
      } catch (error) {
        console.error("Error fetching course:", error)
        setError("Failed to load course details")
      } finally {
        setLoading(false)
      }
    }
    if (params.id) fetchCourse()
  }, [params.id])

  const getCategoryBadge = (type) => {
    const typeStr = (type || "").toLowerCase()
    if (typeStr.includes("university")) return { label: "University", className: "badge-university" }
    if (typeStr.includes("skp")) return { label: "SKP", className: "badge-skp" }
    if (typeStr.includes("language")) return { label: "Language", className: "badge-languages" }
    if (typeStr.includes("ielts")) return { label: "IELTS", className: "badge-ielts" }
    return { label: "Course", className: "badge-default" }
  }

  if (loading) {
    return (
      <div className="course-detail-container">
        <div className="loading-hero" />
      </div>
    )
  }

  if (error || !course) {
    return (
      <div className="course-detail-container">
        <div className="error-state">
          <BookOpen />
          <h2>Course Not Found</h2>
          <button className="back-button" onClick={() => navigate(-1)}>
            <ChevronLeft /> Back to list
          </button>
        </div>
      </div>
    )
  }

  const badge = getCategoryBadge(course.Type)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="course-detail-container"
    >
      <div className="course-detail-wrapper">
        <div className="course-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ChevronLeft size={18} /> Back
          </button>
          <div className="breadcrumb">
            <span>Home</span>
            <span>/</span>
            <span>Courses</span>
            <span>/</span>
            <span className="breadcrumb-current">{course.ProgramName}</span>
          </div>
        </div>

        <div className="course-hero">
          <div className="course-hero-content">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="course-image-section"
            >
              <div className="course-image-large">
                {course.ImageURL ? (
                  <img src={course.ImageURL} alt={course.ProgramName} className="course-image-full-large" />
                ) : (
                  <div className="course-image-fallback-large"><BookOpen /></div>
                )}
              </div>
              <span className={`course-badge-large ${badge.className}`}>{badge.label}</span>
            </motion.div>

            <div className="course-hero-text">
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="course-title-large"
              >
                {course.ProgramName}
              </motion.h1>

              <div className="course-rating-large">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#fbbf24" color="#fbbf24" />
                ))}
                <span className="rating-text-large">4.9 • Impact Level: Advanced</span>
              </div>

              <p className="course-description-large">{course.ProgramDescription}</p>

              <div className="course-quick-info">
                <div className="quick-info-item">
                  <Clock size={18} className="detail-icon" />
                  <span>{course.Duration || "8-12 Weeks"}</span>
                </div>
                <div className="quick-info-item">
                  <GraduationCap size={18} className="detail-icon" />
                  <span>{course.SubType || "Certification"}</span>
                </div>
                <div className="quick-info-item">
                  <Users size={18} className="detail-icon" />
                  <span>Verified Program</span>
                </div>
              </div>

              <button className="enroll-button-large">
                <PlayCircle size={20} /> Enroll for Future
              </button>
            </div>
          </div>
        </div>

        <div className="course-details">
          <div className="course-details-grid">
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="detail-section"
            >
              <h3 className="detail-section-title">Program Specifications</h3>
              <div className="detail-section-content">
                <div className="detail-item">
                  <span className="detail-label">Category</span>
                  <span className="detail-value">{course.Type}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Language</span>
                  <span className="detail-value">English / Global</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Delivery</span>
                  <span className="detail-value">Online / Interactive</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.01 }}
              className="detail-section"
            >
              <h3 className="detail-section-title">What's Included</h3>
              <div className="detail-section-content">
                <div className="learning-item">
                  <CheckCircle size={16} className="check-icon" />
                  <span>Industry-standard Curriculum</span>
                </div>
                <div className="learning-item">
                  <CheckCircle size={16} className="check-icon" />
                  <span>Global Recognition</span>
                </div>
                <div className="learning-item">
                  <CheckCircle size={16} className="check-icon" />
                  <span>Lifetime Community Access</span>
                </div>
              </div>
            </motion.div>

            <div className="enrollment-card">
              <h2 className="enrollment-title">Ready to Elevate Your Career?</h2>
              <p className="enrollment-description">
                Join our elite alumni network and gain the skills that define the next generation of global leaders.
              </p>
              <div className="enrollment-stats">
                <div className="stat-item">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Active Learners</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">4.9/5</span>
                  <span className="stat-label">Member Satisfaction</span>
                </div>
              </div>
              <button className="enroll-button-card">Apply Now</button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default Course
